import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Toast {
    id: string;
    message: string;
    type: 'success' | 'error';
    duration?: number;
}

interface ToastContextType {
    toasts: Toast[];
    addToast: (message: string, type: 'success' | 'error', duration?: number) => void;
    removeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

// "boink boink boink" Sound Generator using Web Audio API
export const playBoinkSound = () => {
    try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        const ctx = new AudioContextClass();
        
        const playSingleBoink = (time: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            
            osc.type = 'triangle'; // Cartoonish bouncy sound
            
            // Frequency sweep for cartoon "boink" bounce: low to high and back
            osc.frequency.setValueAtTime(140, time);
            osc.frequency.exponentialRampToValueAtTime(550, time + 0.12);
            osc.frequency.exponentialRampToValueAtTime(160, time + 0.25);
            
            gain.gain.setValueAtTime(0, time);
            gain.gain.linearRampToValueAtTime(0.35, time + 0.04);
            gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
            
            osc.connect(gain);
            gain.connect(ctx.destination);
            
            osc.start(time);
            osc.stop(time + 0.26);
        };
        
        const now = ctx.currentTime;
        // Play "boink boink boink" (3 rapid consecutive bounces!)
        playSingleBoink(now);
        playSingleBoink(now + 0.15);
        playSingleBoink(now + 0.3);
    } catch (e) {
        console.warn("Web Audio boink sound failed to play", e);
    }
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = (message: string, type: 'success' | 'error', duration = 4000) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts(prev => [...prev, { id, message, type, duration }]);
        
        // Play the "boink boink boink" sound effect!
        playBoinkSound();

        setTimeout(() => {
            removeToast(id);
        }, duration);
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
            {children}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        return {
            toasts: [],
            addToast: (message: string, type: 'success' | 'error') => {
                console.log(`Fallback Toast (${type}): ${message}`);
            },
            removeToast: () => {},
        };
    }
    return context;
};
